<script setup lang="ts">
  import { setBodyOverflow, KEY_ESCAPE } from '@travelata-libs/utils';
  import { onKeyStroke, useResizeObserver } from '@vueuse/core';
  import { computed, onMounted, onBeforeUnmount, ref } from 'vue';
  import Teleport from 'vue2-teleport'; // eslint-disable-line @typescript-eslint/no-unused-vars
  import { ModalDesktopSize } from './ModalDesktop.types';
  import { Button, Typo } from '@/components';

  interface ModalDesktopProps {
    width?: string;
    title?: string;
    closable?: boolean;
    overlayClosable?: boolean;
    zIndex?: number;
    fullSize?: boolean;
    noPadding?: boolean;
    contentClass?: string;
    rootClass?: string;
  }

  interface ModalDesktopEmits {
    (e: 'close'): void;
  }

  const props = withDefaults(defineProps<ModalDesktopProps>(), {
    width: '400px',
    title: '',
    closable: false,
    overlayClosable: true,
    zIndex: 5,
    fullSize: false,
    noPadding: false,
    contentClass: '',
    rootClass: '',
  });

  const emit = defineEmits<ModalDesktopEmits>();
  const modalContentRef = ref<HTMLDivElement | null>(null);
  const contentIsScrollable = ref<boolean>(false);

  const NUMBER_REGEX = /\d+/;

  const updateScrollable = () => {
    const content = modalContentRef.value;

    if (!content) {
      return;
    }

    contentIsScrollable.value = content.scrollHeight > content.clientHeight;
  };

  useResizeObserver(
    [modalContentRef, () => modalContentRef.value?.firstElementChild as HTMLElement | null | undefined],
    updateScrollable,
  );

  const overlayStyle = computed(() => ({
    zIndex: props.zIndex,
  }));

  const contentStyle = computed(() => {
    if (props.fullSize) {
      return {};
    }
    return {
      width: props.width,
    };
  });

  const size = computed(() => {
    const match = props.width.match(NUMBER_REGEX)?.[0];
    const numericWidth = match ? Number(match) : 0;

    if (numericWidth > 779) {
      return ModalDesktopSize.L;
    }

    if (numericWidth > 579) {
      return ModalDesktopSize.M;
    }

    return ModalDesktopSize.S;
  });

  const close = () => {
    emit('close');
  };

  const onOverlayClick = () => {
    if (!props.overlayClosable) {
      return;
    }
    close();
  };

  const resetScroll = () => {
    if (modalContentRef.value) {
      modalContentRef.value?.firstElementChild?.scrollIntoView({
        block: 'start',
        inline: 'nearest',
      });
    }
  };

  onKeyStroke(KEY_ESCAPE, (e) => {
    e.preventDefault();
    close();
  });

  onMounted(() => {
    setBodyOverflow('hidden');
    updateScrollable();
  });

  onBeforeUnmount(() => {
    setBodyOverflow('visible');
  });

  defineExpose({
    resetScroll,
  });
</script>

<template>
  <Teleport to="body">
    <div
      :class="[
        rootClass,
        'tl-modal',
        `tl-modal_size-${size}`,
        {
          'tl-modal_full-size': fullSize,
          'tl-modal_no-padding': noPadding,
        },
      ]"
      data-test-id="modal"
    >
      <div class="tl-modal__overlay" :style="overlayStyle" data-test-id="overlay" @click.self="onOverlayClick">
        <div
          :class="['tl-modal__floating', { 'tl-modal__floating_fullsize': fullSize }]"
          :style="contentStyle"
          data-test-id="floating"
        >
          <Button
            v-if="closable"
            type="icon"
            class="tl-modal__close-btn"
            icon="close"
            appearance="tertiary"
            size="m"
            data-test-id="close-btn"
            @click="close"
          />

          <header
            v-if="$slots.header || title || fullSize"
            :class="['tl-modal__header', { 'tl-modal__header_scrollable': contentIsScrollable }]"
          >
            <slot name="header">
              <Typo v-if="title" class="tl-modal__title" variant="h-5" data-test-id="title">
                {{ title }}
              </Typo>
            </slot>
          </header>

          <div ref="modalContentRef" :class="[contentClass, 'tl-modal__content']">
            <slot />
          </div>

          <footer
            v-if="$slots.footer"
            :class="['tl-modal__footer', { 'tl-modal__footer_scrollable': contentIsScrollable }]"
          >
            <slot name="footer" />
          </footer>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
  .tl-modal {
    $root: &;

    &_full-size {
      #{$root}__floating {
        height: 100vh;
        max-height: 100vh;
        max-width: 100vw;
        width: 100vw;
      }
    }

    &_size {
      &-s,
      &-m {
        #{$root}__floating {
          padding: var(--spacing-xxl) 0 var(--spacing-l);
        }
      }
    }

    &__overlay {
      align-items: center;
      background: var(--color-bg-modal);
      display: flex;
      flex-direction: column;
      height: 100%;
      inset: 0;
      justify-content: center;
      position: fixed;
      width: 100%;
    }

    &__floating {
      background: var(--color-bg-primary);
      border-radius: var(--rounding-l);
      box-shadow: var(--shadow-m);
      box-sizing: border-box;
      color: var(--color-text-primary);
      display: flex;
      flex-direction: column;
      max-height: calc(100% - var(--spacing-xl) - var(--spacing-xl));
      max-width: calc(100% - var(--spacing-xl) - var(--spacing-xl));
      overflow: hidden;
      padding: var(--spacing-xxl) 0;
      position: relative;

      &_fullsize {
        border-radius: 0;
      }

      &:has(#{$root}__content:hover) {
        #{$root}__header_scrollable,
        #{$root}__footer_scrollable {
          box-shadow: var(--shadow-s);
        }
      }
    }

    &__header {
      background-color: inherit;
      clip-path: inset(0 0 -12px 0);
      display: flex;
      padding: 0 var(--spacing-xxl) var(--spacing-l);
      position: relative;
      transition: var(--transition-default);
      z-index: 5;
    }

    &__title {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      width: 100%;
    }

    &__close-btn {
      margin-left: auto;
      position: absolute;
      right: var(--spacing-m);
      top: var(--spacing-m);
      z-index: 6;
    }

    &__content {
      border-right: 6px solid transparent;
      box-sizing: border-box;
      color: var(--color-text-secondary);
      flex: 1;
      overflow-y: auto;
      padding: 0 var(--spacing-l) 0 var(--spacing-xxl);
      scrollbar-color: transparent transparent;
      scrollbar-gutter: stable;

      @include typography-body(l, 400);

      &::-webkit-scrollbar-thumb {
        background-color: transparent;
        transition: var(--transition-default);
      }

      &:hover {
        scrollbar-color: var(--color-neutral-30) transparent;

        &::-webkit-scrollbar-thumb {
          background-color: var(--color-neutral-30);
        }
      }
    }

    &__footer {
      align-items: center;
      background-color: inherit;
      clip-path: inset(-12px 0 0 0);
      display: flex;
      gap: var(--spacing-xs);
      padding: var(--spacing-l) var(--spacing-xxl) 0;
      position: relative;
      transition: var(--transition-default);
      z-index: 5;
    }

    &_no-padding {
      #{$root}__floating {
        padding: 0;
      }

      #{$root}__header,
      #{$root}__content,
      #{$root}__footer {
        padding-left: 0;
        padding-right: 0;
      }
    }
  }
</style>
